// drive-lookup.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpBackend } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

interface DriveResp {
  files: { id: string; name: string }[];
}

interface ListOpts {
  orderBy?: 'name' | 'modifiedTime desc';
  namePrefix?: string;
  sharedDriveId?: string;
}

interface FindOpts {
  sharedDriveId?: string;
}

@Injectable({ providedIn: 'root' })
export class DriveLookupService {
  private readonly URL = 'https://www.googleapis.com/drive/v3/files';

  // rawHttp bypasses interceptors
  private rawHttp: HttpClient;

  constructor(handler: HttpBackend) {
    this.rawHttp = new HttpClient(handler);
  }

  listChildFolders(
    parentFolderId: string,
    apiKey: string,
    opts?: ListOpts
  ): Observable<{ id: string; name: string }[]> {
    let q = `'${parentFolderId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`;
    if (opts && opts.namePrefix) {
      q += ` and name contains '${opts.namePrefix}'`;
    }

    let params = new HttpParams()
      .set('q', q)
      .set('fields', 'files(id,name)')
      .set('orderBy', (opts && opts.orderBy) || 'name')
      .set('key', apiKey);

    if (opts && opts.sharedDriveId) {
      params = params
        .set('supportsAllDrives', 'true')
        .set('includeItemsFromAllDrives', 'true')
        .set('driveId', opts.sharedDriveId)
        .set('corpora', 'drive');
    }

    return this.rawHttp.get<DriveResp>(this.URL, { params }).pipe(
      map(r => r.files || [])
    );
  }

//   findUrnPdf(
//   folderId: string,
//   urn: string,
//   apiKey: string,
//   opts?: FindOpts
// ): Observable<string | null> {
//   // Look for "<PaperCode>_<URN>.pdf"
//   const q = `'${folderId}' in parents and name contains '${urn}' and trashed=false`;

//   let params = new HttpParams()
//     .set('q', q)
//     .set('fields', 'files(id,name)')
//     .set('key', apiKey);

//   if (opts && opts.sharedDriveId) {
//     params = params
//       .set('supportsAllDrives', 'true')
//       .set('includeItemsFromAllDrives', 'true')
//       .set('driveId', opts.sharedDriveId)
//       .set('corpora', 'drive');
//   }

//   return this.rawHttp.get<DriveResp>(this.URL, { params }).pipe(
//     map(r => (r.files && r.files.length ? r.files[0].id : null))
//   );
// }

// inside drive-lookup.service.ts (replace the existing findUrnFile method)
findUrnFile(
  folderId: string,
  pattern: string,   // pattern could be "CSSE07_AnswerKey" or "CSSE07A_2100905ADTT"
  apiKey: string,
  opts?: FindOpts
): Observable<string | null> {
  // allowed extensions we will search for
  const exts = ['pdf', 'jpg', 'jpeg', 'png'];

  // Build a Drive q that requires the pattern and an extension.
  // We create conditions like:
  // (name contains 'CSSE07_AnswerKey' and name contains '.pdf') OR ( ... .jpg ) OR ...
  const extClauses = exts.map(ext => `(name contains '${pattern}' and name contains '.${ext}')`);
  const q = `'${folderId}' in parents and (${extClauses.join(' or ')}) and trashed=false`;

  // build params
  let params = new HttpParams()
    .set('q', q)
    .set('fields', 'files(id,name)')
    .set('key', apiKey);

  if (opts && opts.sharedDriveId) {
    params = params
      .set('supportsAllDrives', 'true')
      .set('includeItemsFromAllDrives', 'true')
      .set('driveId', opts.sharedDriveId)
      .set('corpora', 'drive');
  }

  console.log('[DriveLookupService] findUrnFile query =', q);

  return this.rawHttp.get<DriveResp>(this.URL, { params }).pipe(
    map(r => {
      if (!r || !r.files || !r.files.length) return null;
      // Prefer exact filename matches (case-insensitive), then startsWith, then first containing
      const lowerPattern = pattern.toString().toLowerCase().trim();

      // normalize files list
      const files = r.files.map(f => ({ id: f.id, name: (f.name || '').toString().trim() }));

      // 1) exact match (pattern + ext)
      for (let f of files) {
        const n = f.name.toLowerCase();
        for (let ext of exts) {
          if (n === `${lowerPattern}.${ext}`) return f.id;
        }
      }

      // 2) startsWith (pattern at start) + ext
      for (let f of files) {
        const n = f.name.toLowerCase();
        for (let ext of exts) {
          if (n.startsWith(lowerPattern) && n.endsWith(`.${ext}`)) return f.id;
        }
      }

      // 3) contains pattern + ext
      for (let f of files) {
        const n = f.name.toLowerCase();
        for (let ext of exts) {
          if (n.indexOf(lowerPattern) !== -1 && n.endsWith(`.${ext}`)) return f.id;
        }
      }

      return null;
    })
  );
}



  buildDownloadUrl(fileId: string) {
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }

  listFilesInFolder(folderId: string, apiKey: string): Observable<{ id: string; name: string }[]> {
  let params = new HttpParams()
    .set('q', `'${folderId}' in parents and trashed=false`)
    .set('fields', 'files(id,name)')
    .set('orderBy', 'name')
    .set('key', apiKey);

  return this.rawHttp.get<{ files: { id: string; name: string }[] }>(this.URL, { params })
    .pipe(map(r => r.files || []));
}

findFileByExactName(
  folderId: string,
  fileName: string,
  apiKey: string
): Observable<{ id: string; name: string } | null> {

  const q =
    `'${folderId}' in parents and name='${fileName}' and trashed=false`;

  const params = new HttpParams()
    .set('q', q)
    .set('fields', 'files(id,name)')
    .set('key', apiKey);

  return this.rawHttp.get<DriveResp>(this.URL, { params }).pipe(
    map(r => {
      if (!r || !r.files || !r.files.length) return null;
      return r.files[0];
    })
  );
}


loadJsonFile(
  fileId: string,
  apiKey: string
): Observable<any> {

  const url =
    `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&key=${apiKey}`;

  return this.rawHttp.get(url);
}

}
